import { DateTime } from 'luxon';
import React from 'react';

import { translate } from '@portal/services/i18n';
import { isStatementDayArray } from '@portal/utils/enum';
import {
  formatDateWeekDayMonth,
  formatDateWeekDayMonthYear,
  moneyFormat,
  ZONE,
} from '@portal/utils/format';

import TransactionItem from '@portal/components/TransactionItem/TransactionItem';
import TransactionListSkeleton from '@portal/components/TransactionList/TransactionListSkeleton';
import useReduxState from '@portal/hooks/useReduxState';
import { StatementDay } from '@portal/models/bank';

interface IProps {
  data?:
    | bank.StatementItem[]
    | bank.StatementItemPismo[]
    | bank.StatementDay[]
    | null;
  loading?: boolean;
  isExtract?: boolean;
  isEscrow?: boolean;
  transactionIcon?: boolean;
  transactionTime?: boolean;
  transactionDetails?: boolean;
  longRangeSelectorFormat?: boolean;
  isScheduled?: boolean;
}

const TransactionList: React.FC<IProps> = ({
  data: transactions,
  loading,
  isExtract,
  isEscrow,
  transactionIcon,
  transactionDetails,
  transactionTime,
  longRangeSelectorFormat,
  isScheduled,
}: IProps) => {
  const {
    account: { balance, dailyBalance },
    theme: { currentTheme },
  } = useReduxState();

  const getTransactionDate = (
    transaction: bank.StatementItem | bank.StatementItemPismo
  ): string => {
    return transaction.transactionAt || transaction.transactionScheduleAt || '';
  };

  const sortTransactionsByDate = (
    transactionList: bank.StatementItem[] | bank.StatementItemPismo[]
  ): (bank.StatementItem | bank.StatementItemPismo)[] => {
    return [...transactionList].sort((a, b) => {
      const dateA = getTransactionDate(a);
      const dateB = getTransactionDate(b);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return (
        DateTime.fromISO(dateB, { zone: 'utc' }).toMillis() -
        DateTime.fromISO(dateA, { zone: 'utc' }).toMillis()
      );
    });
  };

  const sortedTransactions =
    transactions && !isStatementDayArray(transactions)
      ? sortTransactionsByDate(transactions)
      : transactions;

  const Empty = () => (
    <div className="transaction-list__empty">
      <div className="transaction-list__empty__icon">📊</div>
      <p>{translate('PAGES.EXTRACT.EMPTY')}</p>
      <small className="transaction-list__empty__subtitle">
        {translate('PAGES.EXTRACT.EMPTY_SUBTITLE')}
      </small>
    </div>
  );

  const getDifferentDates = (
    transactionDate1: string,
    transactionDate2: string
  ) => {
    const date1 = DateTime.fromISO(transactionDate1, { zone: 'utc' }).setZone(
      ZONE
    );
    const date2 = DateTime.fromISO(transactionDate2, { zone: 'utc' }).setZone(
      ZONE
    );

    return date1.toFormat('yyyy-MM-dd') !== date2.toFormat('yyyy-MM-dd');
  };

  const handleDateDivider = (
    transaction: bank.StatementItem | bank.StatementItemPismo,
    prevTransaction: bank.StatementItem | bank.StatementItemPismo
  ) => {
    const date =
      transaction &&
      (transaction.transactionAt
        ? 'transactionAt'
        : transaction.transactionScheduleAt
        ? 'transactionScheduleAt'
        : false);

    const isUtcTransaction = date === 'transactionScheduleAt';

    return date ? (
      <>
        {getDifferentDates(transaction?.[date], prevTransaction?.[date]) &&
          dateItem(transaction?.[date], isUtcTransaction)}
      </>
    ) : (
      <></>
    );
  };

  const dateItem = (date: string, straightUtc?: boolean) => {
    const dailyBalanceItem = getDailyBalanceForDate(date);

    return (
      <div
        className="transaction-list__container__div__date"
        style={{ borderBottom: `1px solid ${currentTheme.borderColor}` }}
      >
        <span>
          {longRangeSelectorFormat
            ? formatDateWeekDayMonthYear(date, straightUtc)
            : formatDateWeekDayMonth(date, straightUtc)}
        </span>
        {dailyBalanceItem ? (
          <span className="transaction-list__container__div__daily-balance">
            {translate('PAGES.EXTRACT.TRANSACTION_ITEM.DAILY_BALANCE')}:{' '}
            {moneyFormat(dailyBalanceItem.balance)}
          </span>
        ) : null}
      </div>
    );
  };

  const isCurrentBalance = (index: number) => balance?.available && index === 0;

  const getDailyBalanceForDate = (
    date: string
  ): models.DailyBalanceItem | undefined => {
    if (!Array.isArray(dailyBalance) || dailyBalance.length === 0)
      return undefined;

    const transactionDate = DateTime.fromISO(date, { zone: 'utc' }).setZone(
      ZONE
    );
    const formattedDate = transactionDate.toFormat('dd/MM/yyyy');

    return dailyBalance.find((item) => item.date === formattedDate);
  };

  const isEmpty = sortedTransactions?.length === 0 || !sortedTransactions;

  return (
    <div className="transaction-list">
      <div className="transaction-list__container">
        {!loading ? (
          isEmpty ? (
            <Empty />
          ) : isStatementDayArray(sortedTransactions) ? (
            sortedTransactions.map((day: StatementDay, index: number) => (
              <React.Fragment key={`${day.date}${index}`}>
                {dateItem(day.date, true)}
                {!!day.totalAmountLastDay && (
                  <TransactionItem
                    directData={{
                      value: moneyFormat(day.totalAmountLastDay),
                      title: translate(
                        'PAGES.EXTRACT.TRANSACTION_ITEM.PREVIOUS_BALANCE'
                      ),
                      description: day.lastDay
                        ? DateTime.fromISO(day.lastDay, {
                            zone: 'utc',
                          }).toLocaleString(DateTime.DATE_SHORT)
                        : '',
                    }}
                    isEscrow={isEscrow}
                    isExtract={isExtract}
                    transactionIcon={false}
                    isScheduled={isScheduled}
                    transactionTime={transactionTime}
                    transactionDetails={transactionDetails}
                  />
                )}
                {day.extract.map((transaction) => (
                  <TransactionItem
                    key={`${day.date}${index}${transaction.id}`}
                    transaction={transaction}
                    isEscrow={isEscrow}
                    isExtract={isExtract}
                    transactionIcon={transactionIcon}
                    transactionTime={transactionTime}
                    isScheduled={isScheduled}
                    transactionDetails={transactionDetails}
                  />
                ))}
                {(!!day.totalAmountDay || isCurrentBalance(index)) && (
                  <TransactionItem
                    directData={{
                      value: moneyFormat(
                        isCurrentBalance(index)
                          ? Number(balance?.available) || 0
                          : Number(day.totalAmountDay) || 0
                      ),
                      title: translate(
                        'PAGES.EXTRACT.TRANSACTION_ITEM.DAILY_BALANCE'
                      ),
                      description: DateTime.fromISO(day.date, {
                        zone: 'utc',
                      }).toLocaleString(DateTime.DATE_SHORT),
                    }}
                    isEscrow={isEscrow}
                    isExtract={isExtract}
                    transactionIcon={false}
                    transactionTime={transactionTime}
                    transactionDetails={transactionDetails}
                    isScheduled={isScheduled}
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            sortedTransactions?.map(
              (
                transaction: bank.StatementItem | bank.StatementItemPismo,
                index: number
              ) => (
                <React.Fragment key={`${transaction.id}${index}`}>
                  {handleDateDivider(
                    transaction,
                    (
                      sortedTransactions as (
                        | bank.StatementItem
                        | bank.StatementItemPismo
                      )[]
                    )[index - 1]
                  )}
                  <TransactionItem
                    transaction={transaction}
                    isEscrow={isEscrow}
                    isExtract={isExtract}
                    transactionIcon={transactionIcon}
                    transactionTime={transactionTime}
                    isScheduled={isScheduled}
                    transactionDetails={transactionDetails}
                  />
                </React.Fragment>
              )
            )
          )
        ) : (
          <TransactionListSkeleton />
        )}
      </div>
    </div>
  );
};

export default TransactionList;
